import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Search, Zap, Upload, PlusCircle, Sparkles, TrendingUp } from 'lucide-react';
import ToolCard from '../components/ToolCard';
import config from '../config';

const BACKEND_URL = config.BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [stats, setStats] = useState({ total_tools: 0, total_categories: 0 });
  const [featuredTools, setFeaturedTools] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, toolsRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/tools`)
      ]);
      
      setStats(statsRes.data);
      setFeaturedTools(toolsRes.data.slice(0, 6));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative px-6 py-20 lg:py-32">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-8 fade-in">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-blue-300 font-medium">AI-Powered Tool Discovery</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
              Your Ultimate
              <span className="block gradient-text mt-2">
                DevOps & Cloud Toolbox
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Intelligence-driven platform for discovering and managing DevOps tools. 
              Leverage AI-powered recommendations, hierarchical taxonomy, and curated solutions 
              to accelerate your infrastructure workflow.
            </p>
            
            {/* Stats */}
            <div className="flex justify-center gap-8 pt-8">
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold gradient-text">{stats.total_tools}</div>
                <div className="text-sm text-gray-400 mt-1">Tools</div>
              </div>
              <div className="w-px bg-gray-700"></div>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-bold gradient-text">{stats.total_categories}</div>
                <div className="text-sm text-gray-400 mt-1">Categories</div>
              </div>
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-wrap justify-center gap-4 pt-8">
              <Link
                to="/ai-search"
                data-testid="hero-ai-search-btn"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-purple-500 via-blue-500 to-blue-600 text-white font-semibold hover:from-purple-600 hover:via-blue-600 hover:to-blue-700 transition-all shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 hover:scale-105 transform"
              >
                <Sparkles className="w-5 h-5" />
                Get AI Recommendations
              </Link>
              
              <Link
                to="/browse"
                data-testid="hero-browse-btn"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gray-800/50 backdrop-blur-sm text-white font-semibold border border-gray-700 hover:bg-gray-700/50 hover:border-blue-500/50 transition-all hover:scale-105 transform"
              >
                <Search className="w-5 h-5" />
                Explore Tools
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-6 py-16 bg-gray-900/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Enterprise-Grade Capabilities</h2>
            <p className="text-gray-400 text-lg">Built for DevOps teams to discover, organize, and leverage the right tools</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Link to="/ai-search" data-testid="feature-ai-search" className="group p-6 rounded-2xl bg-gradient-to-br from-purple-500/5 to-blue-500/5 backdrop-blur-sm border border-gray-700/50 hover:border-purple-500/50 transition-all hover:scale-105 hover:shadow-lg hover:shadow-purple-500/10">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center mb-4 group-hover:from-purple-500/30 group-hover:to-blue-500/30 transition-all">
                <Sparkles className="w-6 h-6 text-purple-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-purple-300">AI Tool Advisor</h3>
              <p className="text-gray-400 text-sm leading-relaxed">Describe your infrastructure challenge and receive intelligent tool recommendations mapped to solution categories</p>
            </Link>
            
            <Link to="/browse" data-testid="feature-browse" className="group p-6 rounded-2xl bg-gradient-to-br from-blue-500/5 to-cyan-500/5 backdrop-blur-sm border border-gray-700/50 hover:border-blue-500/50 transition-all hover:scale-105 hover:shadow-lg hover:shadow-blue-500/10">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center mb-4 group-hover:from-blue-500/30 group-hover:to-cyan-500/30 transition-all">
                <Search className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-blue-300">Explore & Filter</h3>
              <p className="text-gray-400 text-sm leading-relaxed">Navigate through our hierarchical taxonomy with advanced filtering by categories and subtags</p>
            </Link>
            
            <Link to="/upload" data-testid="feature-upload" className="group p-6 rounded-2xl bg-gradient-to-br from-green-500/5 to-emerald-500/5 backdrop-blur-sm border border-gray-700/50 hover:border-green-500/50 transition-all hover:scale-105 hover:shadow-lg hover:shadow-green-500/10">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center mb-4 group-hover:from-green-500/30 group-hover:to-emerald-500/30 transition-all">
                <Upload className="w-6 h-6 text-green-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-green-300">Bulk Import</h3>
              <p className="text-gray-400 text-sm leading-relaxed">Streamline tool onboarding with automated bulk uploads and AI-powered metadata extraction</p>
            </Link>
            
            <Link to="/add-tool" data-testid="feature-add-tool" className="group p-6 rounded-2xl bg-gradient-to-br from-orange-500/5 to-amber-500/5 backdrop-blur-sm border border-gray-700/50 hover:border-orange-500/50 transition-all hover:scale-105 hover:shadow-lg hover:shadow-orange-500/10">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500/20 to-amber-500/20 flex items-center justify-center mb-4 group-hover:from-orange-500/30 group-hover:to-amber-500/30 transition-all">
                <PlusCircle className="w-6 h-6 text-orange-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2 text-orange-300">Curate Tools</h3>
              <p className="text-gray-400 text-sm leading-relaxed">Add tools manually with intelligent URL parsing or leverage AI for automatic information extraction</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Tools Section */}
      {featuredTools.length > 0 && (
        <section className="px-6 py-16">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold mb-2">Curated Tools</h2>
                <p className="text-gray-400">Latest additions to our growing directory</p>
              </div>
              <Link
                to="/browse"
                data-testid="view-all-tools-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30 text-blue-400 font-medium hover:from-blue-500/30 hover:to-purple-500/30 transition-all hover:scale-105"
              >
                Explore All
                <TrendingUp className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredTools.map((tool, index) => (
                <div key={tool.id} className="slide-in" style={{ animationDelay: `${index * 0.1}s` }}>
                  <ToolCard tool={tool} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default HomePage;